/**
 * Client-side UI helpers for the Knowledge Base redesign — kept framework-light
 * (no JSX) so pages/components share one source of truth for type styling and for
 * deriving the structured Ask answer from the existing `{ answer, sources }` API.
 *
 * The synthesis prompt cites excerpts inline as `[n]` (n = source position), so
 * we can wire the citation spine + render clickable markers without a prompt/API
 * change. `tldr` is derived from the first sentence (the spec's sanctioned degrade
 * path); structured points/highlight-ranges are a future enrichment.
 */

import { timeAgo as timeAgoIso } from "@d12labs/utils";
import type { LucideIcon } from "lucide-react";
import { AudioLines, ClipboardList, File, FileType2, NotebookPen } from "lucide-react";
import type {
	DocType,
	KbAnswer,
	KbCitation,
	KbConfidence,
	KbStructuredAnswer,
	QuerySource,
} from "./types";

/** Per-document-type icon, label, and accent tint (tints per the design spec). */
export const DOC_TYPE_META: Record<DocType, { label: string; tint: string; icon: LucideIcon }> = {
	transcript: { label: "Transcript", tint: "var(--color-primary)", icon: AudioLines },
	notes: { label: "Notes", tint: "var(--color-success)", icon: NotebookPen },
	pdf: { label: "PDF", tint: "var(--color-warning)", icon: FileType2 },
	requirements: { label: "Requirements", tint: "var(--color-ink-subtle)", icon: ClipboardList },
	other: { label: "Other", tint: "var(--color-ink-tertiary)", icon: File },
};

/**
 * Compact relative time ("2h ago", "3d ago") — the shared primitive, widened to
 * accept the derived rows' Date-typed timestamps (ISO strings on the wire).
 */
export const timeAgo = (at: string | Date | null | undefined): string =>
	timeAgoIso(at instanceof Date ? at.toISOString() : at);

/** Relevance score (0–100) → confidence band. Tuned for cosine sims (~0.3–0.45). */
export function confidenceFrom(score: number): KbConfidence {
	if (score >= 36) return "high";
	if (score >= 28) return "medium";
	return "low";
}

/**
 * Enrich the structured `/query` answer into the Ask view model: attach the
 * retrieved sources as citations (n = 1-based position, matching the excerpt
 * numbers the synthesis cites) and a confidence band from the top source.
 */
export function deriveAnswer(answer: KbStructuredAnswer, sources: QuerySource[]): KbAnswer {
	const citations: KbCitation[] = sources.map((s, i) => ({
		...s,
		n: i + 1,
		score: Math.round(s.similarity * 100),
	}));
	return {
		...answer,
		confidence: confidenceFrom(citations[0]?.score ?? 0),
		citations,
	};
}

/** A rendered segment of answer text: plain run, bold run, or a `[n]` citation. */
export type RichSegment =
	| { kind: "text"; text: string }
	| { kind: "bold"; text: string }
	| { kind: "cite"; n: number };

/**
 * Tokenize answer text into plain / **bold** / [n]-citation segments so a renderer
 * can interleave clickable citation pills without a full markdown parser (the
 * grounded answers are short prose with bold facts + bracketed citations).
 */
export function tokenizeRichText(text: string): RichSegment[] {
	const segments: RichSegment[] = [];
	// Matches **bold** or a [n] / [n, n] citation group.
	const pattern = /\*\*(.+?)\*\*|\[(\d+(?:\s*,\s*\d+)*)\]/g;
	let last = 0;
	let m: RegExpExecArray | null;
	// biome-ignore lint/suspicious/noAssignInExpressions: standard regex exec loop
	while ((m = pattern.exec(text)) !== null) {
		if (m.index > last) segments.push({ kind: "text", text: text.slice(last, m.index) });
		if (m[1] !== undefined) {
			segments.push({ kind: "bold", text: m[1] });
		} else if (m[2] !== undefined) {
			for (const part of m[2].split(",")) {
				const n = Number.parseInt(part.trim(), 10);
				if (Number.isFinite(n)) segments.push({ kind: "cite", n });
			}
		}
		last = m.index + m[0].length;
	}
	if (last < text.length) segments.push({ kind: "text", text: text.slice(last) });
	return segments;
}

/** Confidence band → Badge tone + label. */
export const CONFIDENCE_META: Record<
	KbConfidence,
	{ tone: "success" | "warning" | "neutral"; label: string }
> = {
	high: { tone: "success", label: "High confidence" },
	medium: { tone: "warning", label: "Medium confidence" },
	low: { tone: "neutral", label: "Low confidence" },
};
